import { ColumnDef } from '@tanstack/react-table'
import { FC } from 'react'
import { SentimentLiftTrendIcon } from '~/features/metric-cell'

import { startCase } from '@hap/utils'
import { toPercent } from '@hap/utils'

import { Monotype, Span } from '../../text'
import { MakeColumnOptions } from './types'

interface Props {
  percent: number | string | null
  precision?: number
}

interface ColumnOptions<D extends UnknownRecord> extends Omit<
  MakeColumnOptions<D>,
  'fieldKey' | 'footer'
> {
  fieldKey: keyof D
  precision?: number
  footer?: number | string | null
}

export const PercentCell: FC<Props> = ({ percent, precision = 0 }) => {
  return (
    <Span nowrap>
      <Monotype>{toPercent(percent, precision)}</Monotype>
    </Span>
  )
}

export function makePercentColumnV2<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey.toString()),
  sortable = true,
  width,
  minWidth,
  precision,
  footer,
}: ColumnOptions<D>): ColumnDef<D> {
  return {
    accessorKey: fieldKey,
    id: fieldKey as string,
    cell: (cell) => (
      <PercentCell percent={cell.getValue<number>()} precision={precision} />
    ),
    header,
    size: width,
    minSize: minWidth,
    meta: {
      align: 'right',
    },
    enableSorting: sortable,
    footer: footer
      ? () => <PercentCell percent={footer} precision={precision} />
      : undefined,
  }
}

export function makePercentTrendColumnV2<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey.toString()),
  precision,
  sortable,
  width = 80,
  minWidth = width,
  maxWidth,
}: ColumnOptions<D>): ColumnDef<D> {
  return {
    accessorKey: fieldKey,
    id: fieldKey as string,
    header,
    size: width,
    minSize: minWidth,
    maxSize: maxWidth,
    enableSorting: sortable,
    meta: {
      align: 'right',
    },
    cell: (cell) => {
      const value = cell.getValue<string>() ? parseFloat(cell.getValue<string>()) : null
      return value ? (
        <span className="inline-flex items-center justify-end gap-2">
          <PercentCell percent={value} precision={precision} />
          {!!value && <SentimentLiftTrendIcon value={value} />}
        </span>
      ) : null
    },
  }
}

export function makeDividerColumnV2<D extends UnknownRecord>({
  numerator,
  denominator,
  header = startCase(numerator.toString()),
  width = 80,
  sortable = true,
}: Omit<MakeColumnOptions<D>, 'fieldKey'> & {
  numerator: keyof D
  denominator: keyof D
}): ColumnDef<D> {
  return {
    header: header,
    size: width,
    id: `${numerator.toString()}-${denominator.toString()}`,
    meta: {
      align: 'right',
    },
    cell: (cell) => {
      const numeratorValue = parseFloat(cell.row.original[numerator] as string)
      const denominatorValue = parseFloat(cell.row.original[denominator] as string)
      if (isNaN(numeratorValue) || isNaN(denominatorValue) || denominatorValue === 0)
        return <PercentCell percent={null} precision={0} />
      return <PercentCell percent={numeratorValue / denominatorValue} />
    },
    enableSorting: sortable,
  }
}
